import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Loader2, MessageSquare, MoreVertical, Paperclip, Play, Plus, Trash2, Users, X } from 'lucide-react';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { DropdownMenu } from '../ui/DropdownMenu';
import { cn } from '../../lib/utils';
import { apiClient } from '../../lib/api-client';
import { useAuth } from '../../contexts/AuthContext';
import type { RoleSummary } from './LeafConfigCard';
import {
  formatRoleChatWorkedFor,
  isLatestRoleChatDetailRequest,
  isRoleChatPinnedToBottom,
  preferredRoleChatCursorEffort,
  preferredRoleChatCursorModel,
  removeRoleChatActiveRun,
  resolveRoleChatIdFromMissionNodes,
  roleChatAssistantLabel,
  roleChatCursorEventLabel,
  shouldReplaceRoleChatMessagesOnDetail,
  shouldShowRoleChatStreamProgress,
} from './role-chat-rendering.utils';
import type { MissionTreeSnapshot } from './MissionTreeView';
import { RoleChatTranscript } from './RoleChatTranscript';

type Visibility = 'private' | 'workspace' | 'shared';
type RoleChatStreamEvent =
  | { type: 'status'; status: string; message?: string }
  | { type: 'run_started'; agentId: string; runId: string }
  | { type: 'cursor_task'; agentId?: string; runId?: string; status?: string; text: string; source?: string; parentRoleCode?: string }
  | { type: 'assistant_delta'; text: string }
  | { type: 'tool_start'; name: string }
  | { type: 'tool_done'; name: string; status: 'completed' | 'error' }
  | { type: 'run_created'; chatRunId: string }
  | { type: 'done'; output: string }
  | { type: 'error'; error: string };
type RoleMissionStreamEvent =
  | { type: 'mission_started'; missionId: string; nodeId?: string; roleCode?: string; mode?: 'foreground' | 'background' }
  | { type: 'node_started'; missionId: string; nodeId: string; roleCode: string; runId?: string }
  | { type: 'assistant_delta'; missionId: string; nodeId: string; roleCode: string; text: string }
  | { type: 'node_delegating'; missionId: string; nodeId: string; fromRoleCode?: string; toRoleCode?: string; task?: string }
  | { type: 'node_awaiting_children'; missionId: string; nodeId: string; roleCode: string }
  | { type: 'node_resumed'; missionId: string; nodeId: string; roleCode: string; childCount?: number }
  | { type: 'node_completed'; missionId: string; nodeId: string; roleCode: string }
  | { type: 'node_failed'; missionId: string; nodeId: string; roleCode: string; error?: string }
  | { type: 'mission_done'; missionId: string; nodeId?: string; roleCode?: string; output?: string }
  | { type: 'mission_snapshot'; missionId: string; snapshot: MissionTreeSnapshot }
  | { type: 'error'; missionId?: string; error: string };
type RoleChatCursorEvent = Record<string, unknown>;
type RoleChatAttachment = {
  id: string;
  kind: 'text' | 'image';
  name: string;
  mimeType: string;
  size: number;
  content?: string;
  data?: string;
};
type RoleChatActiveRun = {
  runId: string;
  status: string;
  source?: string;
  parentRoleCode?: string;
  assistantText?: string;
  error?: string;
  createdAt: string;
  updatedAt: string;
};
type RoleActiveSessionItem = {
  kind: 'chat' | 'mission';
  chatId?: string;
  runId?: string;
  missionId?: string;
  title: string;
  startedAt: string;
  status: string;
  source?: string;
  parentRoleCode?: string;
  roleCode?: string;
};

const MAX_ROLE_CHAT_ATTACHMENTS = 5;
const MAX_ROLE_CHAT_TEXT_BYTES = 256 * 1024;
const MAX_ROLE_CHAT_IMAGE_BYTES = 5 * 1024 * 1024;
const SUPPORTED_IMAGE_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const SUPPORTED_TEXT_EXTENSIONS = new Set([
  '.txt',
  '.md',
  '.markdown',
  '.json',
  '.yaml',
  '.yml',
  '.xml',
  '.csv',
  '.log',
  '.ini',
  '.conf',
  '.properties',
  '.env',
  '.ts',
  '.tsx',
  '.js',
  '.jsx',
  '.py',
  '.sh',
  '.sql',
  '.css',
  '.html',
]);

const ROLE_CHAT_CURSOR_MODEL_STORAGE_KEY = 'aiOrgSetup:roleChat:cursorModel';
const ROLE_CHAT_CURSOR_EFFORT_STORAGE_KEY = 'aiOrgSetup:roleChat:cursorLevel';

type CursorCatalogLevel = { id: string; label: string };
type CursorCatalogModel = { id: string; label?: string; levels?: CursorCatalogLevel[] };

interface RoleChatMeta {
  chatId: string;
  workspaceId: string;
  streamId: string;
  roleCode: string;
  ownerId: string;
  ownerName: string;
  title: string;
  visibility: Visibility;
  sharedWith: string[];
  cursorAgentId?: string;
  cursorStatus?: 'running' | 'finished' | 'error';
  createdAt: string;
  updatedAt: string;
}

interface RoleChatDrawerProps {
  open: boolean;
  onClose: () => void;
  streamId: string;
  role: RoleSummary | null;
  initialChatId?: string | null;
  onOpenRoleChat?: (roleCode: string, chatId: string) => void;
}

function asMeta(row: Record<string, unknown>): RoleChatMeta {
  return row as unknown as RoleChatMeta;
}

function normEmail(s: string): string {
  return String(s || '').trim().toLowerCase();
}

function fileExtension(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx).toLowerCase() : '';
}

function isTextLikeFile(file: File): boolean {
  return file.type.startsWith('text/')
    || file.type === 'application/json'
    || file.type === 'application/yaml'
    || file.type === 'application/x-yaml'
    || file.type === 'application/xml'
    || SUPPORTED_TEXT_EXTENSIONS.has(fileExtension(file.name));
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

function stripDataUrl(dataUrl: string): string {
  const marker = ';base64,';
  const idx = dataUrl.indexOf(marker);
  return idx >= 0 ? dataUrl.slice(idx + marker.length) : dataUrl;
}

export function RoleChatDrawer({
  open,
  onClose,
  streamId,
  role,
  initialChatId,
  onOpenRoleChat,
}: RoleChatDrawerProps) {
  const { user } = useAuth();
  const viewerEmail = normEmail(user?.email || user?.id || '');
  const roleCode = role?.code?.trim() || '';

  const [listTab, setListTab] = useState<'mine' | 'workspace' | 'shared'>('mine');
  const [chats, setChats] = useState<RoleChatMeta[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeChat, setActiveChat] = useState<RoleChatMeta | null>(null);
  const [messages, setMessages] = useState<
    Array<{
      role: 'user' | 'assistant';
      content: string;
      ts: string;
      provider?: 'openai-agents' | 'cursor-cloud-agent';
      attachments?: RoleChatAttachment[];
      cursorEvents?: RoleChatCursorEvent[];
    }>
  >([]);

  const [draft, setDraft] = useState('');
  const [selectedAttachments, setSelectedAttachments] = useState<RoleChatAttachment[]>([]);
  const [sending, setSending] = useState(false);
  const [creatingChat, setCreatingChat] = useState(false);
  const [liveStreaming, setLiveStreaming] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [olderCount, setOlderCount] = useState(0);
  const [activeRuns, setActiveRuns] = useState<RoleChatActiveRun[]>([]);
  const [missionSnapshot, setMissionSnapshot] = useState<MissionTreeSnapshot | null>(null);
  const [activeSessions, setActiveSessions] = useState<RoleActiveSessionItem[]>([]);
  const [isMessagePanePinnedToBottom, setIsMessagePanePinnedToBottom] = useState(true);
  const [watchingMissionId, setWatchingMissionId] = useState<string | null>(null);

  const [selectedCursorModel, setSelectedCursorModel] = useState<string>(() => {
    try {
      const stored = String(window.localStorage.getItem(ROLE_CHAT_CURSOR_MODEL_STORAGE_KEY) || '').trim();
      if (/grok/i.test(stored)) return '';
      return stored;
    } catch {
      return '';
    }
  });
  const [selectedCursorEffort, setSelectedCursorEffort] = useState<string>(() => {
    try {
      return String(window.localStorage.getItem(ROLE_CHAT_CURSOR_EFFORT_STORAGE_KEY) || '').trim();
    } catch {
      return '';
    }
  });
  const [cursorModels, setCursorModels] = useState<CursorCatalogModel[]>([]);
  const [cursorModelsLoading, setCursorModelsLoading] = useState(false);
  const [cursorModelsError, setCursorModelsError] = useState<string | null>(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(ROLE_CHAT_CURSOR_MODEL_STORAGE_KEY, selectedCursorModel);
    } catch {
      // ignore
    }
  }, [selectedCursorModel]);
  useEffect(() => {
    try {
      window.localStorage.setItem(ROLE_CHAT_CURSOR_EFFORT_STORAGE_KEY, selectedCursorEffort);
    } catch {
      // ignore
    }
  }, [selectedCursorEffort]);

  const [shareOpen, setShareOpen] = useState(false);
  const [shareEmails, setShareEmails] = useState('');

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTitle, setRenameTitle] = useState('');

  type WorkflowRunState =
    | { status: 'running' }
    | { status: 'done'; runId: string }
    | { status: 'error'; error: string };
  const [workflowRuns, setWorkflowRuns] = useState<Record<number, WorkflowRunState>>({});
  const [workflowStepExecutionByMessage, setWorkflowStepExecutionByMessage] = useState<
    Record<number, 'independent_agents' | 'same_chat_followups'>
  >({});

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagePanePinnedRef = useRef(true);
  const sendAbortRef = useRef<AbortController | null>(null);
  const activeRunAbortRef = useRef<AbortController | null>(null);
  const missionAbortRef = useRef<AbortController | null>(null);
  const chatMissionInFlightRef = useRef(false);
  const reconnectingRunIdRef = useRef<string | null>(null);
  const liveStreamingRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const detailRequestSeqRef = useRef(0);

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    const el = messagesContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    messagePanePinnedRef.current = true;
    setIsMessagePanePinnedToBottom(true);
  };

  const updateMessagePanePinnedState = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const pinned = isRoleChatPinnedToBottom({
      scrollHeight: el.scrollHeight,
      scrollTop: el.scrollTop,
      clientHeight: el.clientHeight,
    });
    messagePanePinnedRef.current = pinned;
    setIsMessagePanePinnedToBottom(pinned);
  }, []);

  const applyRoleChatStreamEvent = useCallback((
    event: RoleChatStreamEvent,
    assistantTs: string,
    appendStep: (label: string) => void,
  ) => {
    const appendAssistantActivity = (activity: RoleChatCursorEvent) => {
      const label = roleChatCursorEventLabel(activity).trim();
      if (!label) return;
      setMessages((prev) => prev.map((msg) => (
        msg.ts === assistantTs
          ? { ...msg, cursorEvents: [...(msg.cursorEvents || []), activity] }
          : msg
      )));
    };

    if (event.type === 'run_created') {
      return;
    }
    if (event.type === 'status') {
      appendStep(event.message || event.status.replace(/_/g, ' '));
      appendAssistantActivity(event as unknown as RoleChatCursorEvent);
    } else if (event.type === 'run_started') {
      appendStep('AI agent connected');
      appendAssistantActivity(event as unknown as RoleChatCursorEvent);
    } else if (event.type === 'cursor_task') {
      const label = roleChatCursorEventLabel(event as unknown as RoleChatCursorEvent);
      appendStep(label || 'Delegated task activity');
      appendAssistantActivity(event as unknown as RoleChatCursorEvent);
    } else if (event.type === 'tool_start') {
      appendStep(`Calling ${event.name}`);
      appendAssistantActivity(event as unknown as RoleChatCursorEvent);
    } else if (event.type === 'tool_done') {
      appendStep(`${event.name} ${event.status === 'completed' ? 'completed' : 'failed'}`);
      appendAssistantActivity(event as unknown as RoleChatCursorEvent);
    } else if (event.type === 'assistant_delta') {
      const delta = { type: 'assistant_delta', text: event.text } as RoleChatCursorEvent;
      setMessages((prev) => {
        const existing = prev.find((msg) => msg.ts === assistantTs);
        if (!existing) {
          return [
            ...prev,
            {
              role: 'assistant',
              content: event.text,
              ts: assistantTs,
              provider: 'cursor-cloud-agent',
              cursorEvents: [delta],
            },
          ];
        }
        return prev.map((msg) => (
          msg.ts === assistantTs
            ? {
              ...msg,
              content: `${msg.content}${event.text}`,
              cursorEvents: [...(msg.cursorEvents || []), delta],
            }
            : msg
        ));
      });
    } else if (event.type === 'done') {
      setMessages((prev) => {
        const existing = prev.find((msg) => msg.ts === assistantTs);
        if (!existing) {
          return [
            ...prev,
            {
              role: 'assistant',
              content: event.output || '',
              ts: assistantTs,
              provider: 'cursor-cloud-agent',
            },
          ];
        }
        return prev.map((msg) => (
          msg.ts === assistantTs ? { ...msg, content: event.output || msg.content } : msg
        ));
      });
      appendStep('Response complete');
    } else if (event.type === 'error') {
      throw new Error(event.error || 'AI response failed');
    }
  }, []);

  const loadChats = useCallback(async (opts?: { silent?: boolean }) => {
    if (!roleCode || !streamId) return;
    if (!opts?.silent) {
      setListLoading(true);
      setListError(null);
    }
    try {
      const res = await apiClient.listAiRoleChats(streamId, roleCode, listTab);
      setChats((res.chats || []).map(asMeta));
    } catch (e: any) {
      if (!opts?.silent) {
        setListError(e.message || 'Failed to load chats');
        setChats([]);
      }
    } finally {
      if (!opts?.silent) setListLoading(false);
    }
  }, [streamId, roleCode, listTab]);

  const loadActiveSessions = useCallback(async () => {
    if (!roleCode || !streamId) return;
    try {
      const res = await apiClient.getAiRoleActiveChats(streamId, roleCode);
      setActiveSessions(res.items || []);
    } catch {
      setActiveSessions([]);
    }
  }, [roleCode, streamId]);

  const refreshChatMission = useCallback(async (chatId: string) => {
    if (chatMissionInFlightRef.current) return;
    chatMissionInFlightRef.current = true;
    try {
      const res = await apiClient.getAiChatMission(chatId);
      setMissionSnapshot(res && res.mission ? (res as unknown as MissionTreeSnapshot) : null);
    } catch {
      // mission tree is best-effort observability; ignore failures
    } finally {
      chatMissionInFlightRef.current = false;
    }
  }, []);

  const openRoleChat = useCallback((targetRoleCode: string, chatId: string) => {
    if (!chatId) return;
    if (targetRoleCode && targetRoleCode !== roleCode && onOpenRoleChat) {
      onOpenRoleChat(targetRoleCode, chatId);
      return;
    }
    setSelectedChatId(chatId);
  }, [onOpenRoleChat, roleCode]);

  const watchMission = useCallback(async (missionId: string) => {
    missionAbortRef.current?.abort();
    const controller = new AbortController();
    missionAbortRef.current = controller;
    setWatchingMissionId(missionId);
    setSendError(null);
    try {
      const snapshot = await apiClient.getAiMission(missionId);
      setMissionSnapshot(snapshot as unknown as MissionTreeSnapshot);
      const existingEvents = Array.isArray(snapshot.events) ? snapshot.events.length : 0;
      await apiClient.streamAiMission(
        missionId,
        (event: RoleMissionStreamEvent) => {
          if (event.type === 'mission_snapshot' && event.snapshot) {
            setMissionSnapshot(event.snapshot);
          } else if (event.type === 'mission_done') {
            void apiClient.getAiMission(missionId)
              .then((latest) => setMissionSnapshot(latest as unknown as MissionTreeSnapshot))
              .catch(() => undefined);
          }
        },
        controller.signal,
        existingEvents,
      );
    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        setSendError(error.message || 'Failed to stream mission');
      }
    } finally {
      if (missionAbortRef.current === controller) missionAbortRef.current = null;
      setWatchingMissionId(null);
      void loadActiveSessions();
    }
  }, [loadActiveSessions]);

  const openActiveSession = useCallback((item: RoleActiveSessionItem) => {
    if (item.chatId) {
      openRoleChat(item.roleCode || roleCode, item.chatId);
    }
    if (item.kind === 'mission' && item.missionId) {
      void watchMission(item.missionId);
    }
  }, [openRoleChat, roleCode, watchMission]);

  const resumeActiveRun = useCallback(
    async (chatId: string, run: RoleChatActiveRun) => {
      if (reconnectingRunIdRef.current === run.runId) return;
      reconnectingRunIdRef.current = run.runId;
      liveStreamingRef.current = true;
      setLiveStreaming(true);
      setSendError(null);
      const assistantTs = `active-${run.runId}`;
      setMessages((prev) => (
        prev.some((msg) => msg.ts === assistantTs)
          ? prev
          : [
            ...prev,
            {
              role: 'assistant',
              content: '',
              ts: assistantTs,
              provider: 'cursor-cloud-agent',
            },
          ]
      ));

      const controller = new AbortController();
      activeRunAbortRef.current = controller;
      const appendStep = (_label: string) => undefined;

      try {
        await apiClient.streamAiRoleChatRun(
          chatId,
          run.runId,
          (event: RoleChatStreamEvent) => applyRoleChatStreamEvent(event, assistantTs, appendStep),
          controller.signal,
        );
        const res = await apiClient.getAiRoleChat(chatId);
        setActiveChat(asMeta(res.chat as Record<string, unknown>));
        setMessages(
          (res.messages || []).map((m) => ({
            role: m.role === 'user' ? 'user' : 'assistant',
            content: m.content,
            ts: m.ts,
            provider: m.provider,
            attachments: m.attachments,
            cursorEvents: m.cursorEvents,
          })),
        );
        setActiveRuns((res.activeRuns || []) as RoleChatActiveRun[]);
        await loadChats();
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          setSendError(error.message || 'Failed to resume active response');
          setActiveRuns((prev) => removeRoleChatActiveRun(prev, run.runId));
        }
      } finally {
        if (activeRunAbortRef.current === controller) {
          activeRunAbortRef.current = null;
          reconnectingRunIdRef.current = null;
          liveStreamingRef.current = false;
          setLiveStreaming(false);
        }
      }
    },
    [applyRoleChatStreamEvent, loadChats],
  );

  const loadDetail = useCallback(
    async (chatId: string, opts?: { silent?: boolean }) => {
      const requestSeq = detailRequestSeqRef.current + 1;
      detailRequestSeqRef.current = requestSeq;
      if (!opts?.silent) {
        setDetailLoading(true);
        setSendError(null);
      }
      try {
        const res = await apiClient.getAiRoleChat(chatId);
        if (!isLatestRoleChatDetailRequest(detailRequestSeqRef.current, requestSeq)) return;
        setActiveChat(asMeta(res.chat as Record<string, unknown>));
        if (shouldReplaceRoleChatMessagesOnDetail({
          silent: opts?.silent,
          liveStreaming: liveStreamingRef.current,
        })) {
          setMessages(
            (res.messages || []).map((m) => ({
              role: m.role === 'user' ? 'user' : 'assistant',
              content: m.content,
              ts: m.ts,
              provider: m.provider,
              attachments: m.attachments,
              cursorEvents: m.cursorEvents,
            })),
          );
        }
        setActiveRuns((res.activeRuns || []) as RoleChatActiveRun[]);
        setOlderCount(Number(res.olderCount || 0));
      } catch (e: any) {
        if (!isLatestRoleChatDetailRequest(detailRequestSeqRef.current, requestSeq)) return;
        if (!opts?.silent) {
          setSendError(e.message || 'Failed to load conversation');
          setActiveChat(null);
          setMessages([]);
          setActiveRuns([]);
          setOlderCount(0);
        }
      } finally {
        if (!opts?.silent && isLatestRoleChatDetailRequest(detailRequestSeqRef.current, requestSeq)) {
          setDetailLoading(false);
        }
      }
    },
    [],
  );

  const loadCursorModels = useCallback(async () => {
    if (!roleCode || !streamId) return;
    setCursorModelsLoading(true);
    setCursorModelsError(null);
    try {
      const response = await apiClient.getAiRoleChatModels(streamId, roleCode);
      const models = Array.isArray(response?.cursor?.models) ? response.cursor.models : [];
      setCursorModels(models);
      const nextModel = preferredRoleChatCursorModel(models, selectedCursorModel);
      if (nextModel !== selectedCursorModel) setSelectedCursorModel(nextModel);
      const activeModel = models.find((model) => model.id === nextModel) || models[0];
      const levels = activeModel?.levels || [];
      if (levels.length > 0 && !levels.some((level) => level.id === selectedCursorEffort)) {
        setSelectedCursorEffort(preferredRoleChatCursorEffort(levels, ''));
      }
      if (levels.length === 0) setSelectedCursorEffort('');
    } catch (error: any) {
      setCursorModels([]);
      setCursorModelsError(error.message || 'Failed to load AI models');
    } finally {
      setCursorModelsLoading(false);
    }
  }, [roleCode, streamId, selectedCursorModel, selectedCursorEffort]);

  useEffect(() => {
    if (!open) {
      activeRunAbortRef.current?.abort();
      missionAbortRef.current?.abort();
      setSelectedChatId(null);
      setActiveChat(null);
      setMessages([]);
      setActiveRuns([]);
      setMissionSnapshot(null);
      setActiveSessions([]);
      setWatchingMissionId(null);
      setDraft('');
      setLiveStreaming(false);
      liveStreamingRef.current = false;
      return;
    }
    void loadChats();
    void loadActiveSessions();
  }, [open, loadChats, loadActiveSessions]);

  useEffect(() => {
    if (!open || !initialChatId) return;
    setSelectedChatId(initialChatId);
  }, [open, initialChatId, roleCode]);

  useEffect(() => {
    if (!open || !selectedChatId) return;
    activeRunAbortRef.current?.abort();
    activeRunAbortRef.current = null;
    reconnectingRunIdRef.current = null;
    liveStreamingRef.current = false;
    setLiveStreaming(false);
    setActiveRuns([]);
    void loadDetail(selectedChatId);
  }, [open, selectedChatId, loadDetail]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setInterval(() => {
      if (listLoading) return;
      void loadChats({ silent: true });
      void loadActiveSessions();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [open, loadChats, loadActiveSessions, listLoading]);

  useEffect(() => {
    if (!open || !selectedChatId || sending || liveStreaming) return;
    const timer = window.setInterval(() => {
      void loadDetail(selectedChatId, { silent: true });
    }, 2500);
    return () => window.clearInterval(timer);
  }, [open, selectedChatId, sending, liveStreaming, loadDetail]);

  useEffect(() => {
    if (!open || !selectedChatId || sending || liveStreaming) return;
    const run = activeRuns.find((item) => item.status === 'queued' || item.status === 'running');
    if (!run) return;
    setSendError(null);
    void resumeActiveRun(selectedChatId, run);
  }, [open, selectedChatId, sending, liveStreaming, activeRuns, resumeActiveRun]);

  useEffect(() => {
    if (!open) return;
    void loadCursorModels();
  }, [open, loadCursorModels]);

  // Reset per-turn workflow run state when the active chat changes; runIds are conversation-specific.
  useEffect(() => {
    setWorkflowRuns({});
    setMissionSnapshot(null);
    setWatchingMissionId(null);
  }, [selectedChatId]);

  // Load any recorded delegation tree for the selected chat (populated when the role delegates).
  useEffect(() => {
    if (!open || !selectedChatId) return;
    void refreshChatMission(selectedChatId);
    const timer = window.setInterval(() => {
      void refreshChatMission(selectedChatId);
    }, 8000);
    return () => window.clearInterval(timer);
  }, [open, selectedChatId, refreshChatMission]);

  useEffect(() => {
    if (!messagePanePinnedRef.current) return;
    scrollToBottom('auto');
  }, [messages, sending]);

  useEffect(() => {
    messagePanePinnedRef.current = true;
    setIsMessagePanePinnedToBottom(true);
    window.requestAnimationFrame(() => scrollToBottom('auto'));
  }, [selectedChatId]);

  const selectedCursorModelDef = cursorModels.find((model) => model.id === selectedCursorModel);
  const selectedCursorEffortOptions = selectedCursorModelDef?.levels || [];

  useEffect(() => {
    if (selectedCursorEffortOptions.length === 0) {
      if (selectedCursorEffort) setSelectedCursorEffort('');
      return;
    }
    if (!selectedCursorEffortOptions.some((level) => level.id === selectedCursorEffort)) {
      setSelectedCursorEffort(preferredRoleChatCursorEffort(selectedCursorEffortOptions, ''));
    }
  }, [selectedCursorEffortOptions, selectedCursorEffort]);

  const isOwner = activeChat ? normEmail(activeChat.ownerId) === viewerEmail : false;

  const handleAttachFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setSendError(null);
    try {
      const currentCount = selectedAttachments.length;
      const incoming = Array.from(files);
      if (currentCount + incoming.length > MAX_ROLE_CHAT_ATTACHMENTS) {
        throw new Error(`Attach at most ${MAX_ROLE_CHAT_ATTACHMENTS} files.`);
      }

      const next: RoleChatAttachment[] = [];
      for (const file of incoming) {
        if (SUPPORTED_IMAGE_MIME_TYPES.has(file.type)) {
          if (file.size > MAX_ROLE_CHAT_IMAGE_BYTES) {
            throw new Error(`${file.name} must be 5 MB or smaller.`);
          }
          const dataUrl = await readFileAsDataUrl(file);
          next.push({
            id: `att-${Date.now()}-${Math.random().toString(16).slice(2)}`,
            kind: 'image',
            name: file.name,
            mimeType: file.type,
            size: file.size,
            data: stripDataUrl(dataUrl),
          });
          continue;
        }

        if (isTextLikeFile(file)) {
          if (file.size > MAX_ROLE_CHAT_TEXT_BYTES) {
            throw new Error(`${file.name} must be 256 KB or smaller.`);
          }
          next.push({
            id: `att-${Date.now()}-${Math.random().toString(16).slice(2)}`,
            kind: 'text',
            name: file.name,
            mimeType: file.type || 'text/plain',
            size: file.size,
            content: await file.text(),
          });
          continue;
        }

        throw new Error(`${file.name} is not supported by the AI agent attachment flow.`);
      }
      setSelectedAttachments((prev) => [...prev, ...next]);
    } catch (error: any) {
      setSendError(error.message || 'Failed to attach file');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeSelectedAttachment = (id: string) => {
    setSelectedAttachments((prev) => prev.filter((attachment) => attachment.id !== id));
  };

  const createMineChat = async (): Promise<string | null> => {
    if (!roleCode) return null;
    const res = await apiClient.createAiRoleChat(streamId, roleCode, { visibility: 'private' });
    const meta = asMeta(res.chat as Record<string, unknown>);
    setSelectedChatId(meta.chatId);
    setListTab('mine');
    await loadChats();
    return meta.chatId;
  };

  const handleNewChat = async () => {
    if (!roleCode || creatingChat) return;
    setCreatingChat(true);
    setListError(null);
    try {
      await createMineChat();
    } catch (e: any) {
      setListError(e.message || 'Failed to create chat');
    } finally {
      setCreatingChat(false);
    }
  };

  const handleSend = async () => {
    const text = draft.trim();
    const attachmentsForSend = selectedAttachments;
    if (!text && attachmentsForSend.length === 0) return;
    if (!selectedCursorModel) {
      setSendError(cursorModelsError || 'AI model list is still loading.');
      return;
    }
    if (sending && !reconnectingRunIdRef.current && !liveStreaming) return;
    activeRunAbortRef.current?.abort();
    activeRunAbortRef.current = null;
    reconnectingRunIdRef.current = null;
    liveStreamingRef.current = false;
    setLiveStreaming(false);
    const messageText = text || 'Please review the attached file(s).';
    const draftBeforeSend = draft;
    setSending(true);
    setSendError(null);
    let chatId = selectedChatId;
    try {
      if (!chatId) {
        chatId = await createMineChat();
        if (!chatId) throw new Error('Failed to create chat');
      }
      const userTs = new Date().toISOString();
      const assistantTs = new Date(Date.now() + 1).toISOString();
      setMessages((prev) => [
        ...prev,
        {
          role: 'user',
          content: messageText,
          ts: userTs,
          attachments: attachmentsForSend.length > 0 ? attachmentsForSend : undefined,
        },
        { role: 'assistant', content: '', ts: assistantTs, provider: 'cursor-cloud-agent' },
      ]);
      setDraft('');
      setSelectedAttachments([]);
      const controller = new AbortController();
      sendAbortRef.current = controller;
      const appendStep = (_label: string) => undefined;

      await apiClient.streamAiRoleChatMessage(
        chatId,
        messageText,
        {
          provider: 'cursor',
          cursorModel: selectedCursorModel,
          cursorEffort: selectedCursorEffort,
          attachments: attachmentsForSend.length > 0 ? attachmentsForSend : undefined,
        },
        (event: RoleChatStreamEvent) => applyRoleChatStreamEvent(event, assistantTs, appendStep),
        controller.signal,
      );

      await loadDetail(chatId);
      await loadChats();
      await loadActiveSessions();
      await refreshChatMission(chatId);
    } catch (e: any) {
      const message = e?.name === 'AbortError' ? 'Message stopped' : e.message || 'Send failed';
      if (chatId) {
        await loadDetail(chatId);
        await refreshChatMission(chatId);
      } else {
        setDraft((current) => current || draftBeforeSend);
        setSelectedAttachments((current) => (current.length > 0 ? current : attachmentsForSend));
      }
      setSendError(message);
      await loadActiveSessions();
    } finally {
      sendAbortRef.current = null;
      setSending(false);
    }
  };

  const handleStopSending = () => {
    sendAbortRef.current?.abort();
  };

  const handleDelete = async (chatId: string) => {
    if (!window.confirm('Delete this conversation permanently?')) return;
    try {
      await apiClient.deleteAiRoleChat(chatId);
      if (selectedChatId === chatId) {
        setSelectedChatId(null);
        setActiveChat(null);
        setMessages([]);
      }
      await loadChats();
    } catch (e: any) {
      setListError(e.message || 'Delete failed');
    }
  };

  const handleSaveRename = async () => {
    if (!activeChat) return;
    try {
      await apiClient.patchAiRoleChat(activeChat.chatId, { title: renameTitle.trim() || 'New chat' });
      setRenameOpen(false);
      await loadDetail(activeChat.chatId);
      await loadChats();
    } catch (e: any) {
      setSendError(e.message || 'Rename failed');
    }
  };

  const handleVisibilityChange = async (v: Visibility) => {
    if (!activeChat || !isOwner) return;
    try {
      await apiClient.patchAiRoleChat(activeChat.chatId, {
        visibility: v,
        sharedWith: v === 'shared' ? activeChat.sharedWith : [],
      });
      await loadDetail(activeChat.chatId);
      await loadChats();
    } catch (e: any) {
      setSendError(e.message || 'Update failed');
    }
  };

  const handleRunWorkflow = async (idx: number) => {
    if (!selectedChatId || !isOwner) return;
    const current = workflowRuns[idx];
    if (current && (current.status === 'running' || current.status === 'done')) return;
    setWorkflowRuns((prev) => ({ ...prev, [idx]: { status: 'running' } }));
    try {
      const stepExecution = workflowStepExecutionByMessage[idx];
      const res = await apiClient.runAiRoleChatWorkflow(
        selectedChatId,
        idx,
        stepExecution ? { stepExecution } : undefined,
      );
      setWorkflowRuns((prev) => ({ ...prev, [idx]: { status: 'done', runId: res.runId } }));
    } catch (e: any) {
      setWorkflowRuns((prev) => ({
        ...prev,
        [idx]: { status: 'error', error: e?.message || 'Failed to trigger workflow' },
      }));
    }
  };

  const hasPrecedingUserMessage = (idx: number): boolean => {
    for (let i = idx - 1; i >= 0; i--) {
      if (messages[i].role === 'user') return true;
    }
    return false;
  };

  const handleSaveShare = async () => {
    if (!activeChat) return;
    const emails = shareEmails
      .split(/[,;\s]+/)
      .map((x) => normEmail(x))
      .filter(Boolean);
    try {
      await apiClient.patchAiRoleChat(activeChat.chatId, {
        visibility: 'shared',
        sharedWith: emails,
      });
      setShareOpen(false);
      setShareEmails('');
      await loadDetail(activeChat.chatId);
      await loadChats();
    } catch (e: any) {
      setSendError(e.message || 'Share update failed');
    }
  };

  const visibilityLabel = (v: Visibility) => {
    if (v === 'private') return 'Private';
    if (v === 'workspace') return 'Workspace';
    return 'Shared';
  };

  const attachmentLabel = (attachment: RoleChatAttachment): string => {
    const kb = Math.max(1, Math.round(attachment.size / 1024));
    return `${attachment.name} (${attachment.kind}, ${kb} KB)`;
  };

  const runtimeLabel = `AI agent${selectedCursorModel ? ` (${selectedCursorModel})` : ''}`;
  const activeChatIds = useMemo(
    () => new Set(activeSessions.filter((item) => item.kind === 'chat').map((item) => item.chatId || '').filter(Boolean)),
    [activeSessions],
  );
  const activeChatSessionsById = useMemo(() => {
    const map = new Map<string, RoleActiveSessionItem>();
    activeSessions
      .filter((item) => item.kind === 'chat' && item.chatId)
      .forEach((item) => map.set(item.chatId!, item));
    return map;
  }, [activeSessions]);
  const activeSessionItems = useMemo(
    () => activeSessions.filter((item) => item.chatId || item.missionId).slice(0, 6),
    [activeSessions],
  );

  const roleLabel = role?.agentTitle || role?.code || 'Role';
  const messageProviderLabel = (provider?: 'openai-agents' | 'cursor-cloud-agent') =>
    roleChatAssistantLabel(provider, roleLabel);

  if (!open || !role || !roleCode) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent
          maxWidth="6xl"
          className="!max-w-[min(96vw,1152px)] h-[min(90vh,880px)] p-0 flex flex-row overflow-hidden"
        >
          <div className="flex w-72 shrink-0 flex-col border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950">
            <div className="border-b border-gray-200 dark:border-gray-700 px-3 py-3 space-y-2">
              <div className="pr-8">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate" title={roleLabel}>
                  Chat · {roleLabel}
                </h2>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 font-mono truncate">{roleCode}</p>
              </div>
              <div className="flex rounded-lg bg-gray-200/80 dark:bg-gray-800 p-0.5">
                {(['mine', 'workspace', 'shared'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={cn(
                      'flex-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors',
                      listTab === t
                        ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200',
                    )}
                    onClick={() => setListTab(t)}
                  >
                    {t === 'mine' ? 'Mine' : t === 'workspace' ? 'Workspace' : 'Shared'}
                  </button>
                ))}
              </div>
              <Button
                type="button"
                size="sm"
                className="w-full gap-2"
                variant="outline"
                disabled={creatingChat}
                onClick={() => void handleNewChat()}
              >
                {creatingChat ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                New chat
              </Button>
              {activeSessionItems.length > 0 ? (
                <div className="space-y-1 rounded-lg border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-950/20 p-2">
                  <p className="text-[11px] font-medium text-emerald-800 dark:text-emerald-200">Active runs</p>
                  {activeSessionItems.map((item) => (
                    <button
                      key={item.missionId || item.chatId || item.runId}
                      type="button"
                      className={cn(
                        'w-full rounded-md px-2 py-1 text-left text-[11px] transition-colors',
                        (item.chatId && selectedChatId === item.chatId)
                          || (item.missionId && watchingMissionId === item.missionId)
                          ? 'bg-emerald-200/80 dark:bg-emerald-900/50 text-emerald-900 dark:text-emerald-100'
                          : 'text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40',
                      )}
                      onClick={() => openActiveSession(item)}
                    >
                      <span className="line-clamp-1">{item.title}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {listLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : listError ? (
                <p className="p-3 text-xs text-red-600 dark:text-red-400">{listError}</p>
              ) : chats.length === 0 ? (
                <p className="p-3 text-xs text-gray-500 dark:text-gray-400">No conversations in this tab.</p>
              ) : (
                <ul className="p-2 space-y-1">
                  {chats.map((c) => (
                    <li key={c.chatId}>
                      <div
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedChatId(c.chatId);
                          }
                        }}
                        onClick={() => setSelectedChatId(c.chatId)}
                        className={cn(
                          'w-full rounded-lg px-2 py-2 text-left text-xs transition-colors cursor-pointer',
                          selectedChatId === c.chatId
                            ? 'bg-violet-100 dark:bg-violet-900/40 text-gray-900 dark:text-gray-100'
                            : 'hover:bg-gray-200/80 dark:hover:bg-gray-800',
                        )}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <span className="font-medium line-clamp-2">{c.title}</span>
                          {normEmail(c.ownerId) === viewerEmail ? (
                            <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                              <DropdownMenu
                                align="end"
                                trigger={
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 shrink-0"
                                    aria-label="Chat list actions"
                                  >
                                    <MoreVertical className="w-3.5 h-3.5" />
                                  </Button>
                                }
                                items={[
                                  {
                                    label: 'Rename',
                                    onClick: () => {
                                      setRenameTitle(c.title);
                                      setSelectedChatId(c.chatId);
                                      setRenameOpen(true);
                                    },
                                  },
                                  {
                                    label: 'Delete',
                                    icon: <Trash2 className="w-4 h-4" />,
                                    onClick: () => void handleDelete(c.chatId),
                                    className: 'text-red-600 dark:text-red-400',
                                  },
                                ]}
                              />
                            </div>
                          ) : null}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {c.cursorStatus === 'running' || activeChatIds.has(c.chatId) ? (
                            <Badge variant="success" className="text-[10px]">
                              {c.cursorStatus === 'running'
                                ? 'Running'
                                : activeChatSessionsById.get(c.chatId)?.source === 'cursor_native_delegation'
                                  ? 'Cursor native delegation'
                                  : 'Active'}
                            </Badge>
                          ) : null}
                          <Badge variant="outline" className="text-[10px]">
                            {visibilityLabel(c.visibility)}
                          </Badge>
                          <span className="text-[10px] text-gray-400">
                            {new Date(c.updatedAt).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-white dark:bg-gray-900">
            {/* pr-14 clears DialogContent absolute close (top-right) */}
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 dark:border-gray-700 px-4 py-3 pr-14">
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {activeChat?.title || 'Select a chat'}
                </h3>
                {activeChat ? (
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        Owner: {activeChat.ownerName || activeChat.ownerId}
                      </p>
                      {activeChat.cursorStatus === 'running' ? (
                        <Badge variant="success" className="text-[10px]">Running</Badge>
                      ) : null}
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      Runtime: {runtimeLabel}
                    </p>
                  </div>
                ) : null}
              </div>
              {activeChat && isOwner ? (
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                  <label className="text-[11px] text-gray-500 dark:text-gray-400 sr-only">Visibility</label>
                  <select
                    className="h-8 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 text-xs"
                    value={activeChat.visibility}
                    onChange={(e) => void handleVisibilityChange(e.target.value as Visibility)}
                  >
                    <option value="private">Private</option>
                    <option value="workspace">Workspace</option>
                    <option value="shared">Shared</option>
                  </select>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="gap-1"
                    onClick={() => {
                      setShareEmails(activeChat.sharedWith.join(', '));
                      setShareOpen(true);
                    }}
                  >
                    <Users className="w-3.5 h-3.5" />
                    Share
                  </Button>
                </div>
              ) : null}
            </div>

            <div className="relative min-h-0 flex-1">
              <div
                ref={messagesContainerRef}
                className="h-full overflow-y-auto p-4 space-y-4"
                onScroll={updateMessagePanePinnedState}
              >
                {detailLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                  </div>
                ) : !selectedChatId ? (
                  <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-sm text-gray-500 dark:text-gray-400">
                    <MessageSquare className="w-10 h-10 opacity-40" />
                    <p>Select a chat or start a new one.</p>
                  </div>
                ) : (
                  <>
                  {olderCount > 0 ? (
                    <p className="text-center text-[11px] text-gray-500 dark:text-gray-400">
                      Showing the latest {messages.length} messages ({olderCount} earlier)
                    </p>
                  ) : null}
                  {messages.map((msg, i) => {
                    const runState = workflowRuns[i];
                    const showRunButton =
                      msg.role === 'assistant' && isOwner && hasPrecedingUserMessage(i);
                    const previousUser = [...messages.slice(0, i)].reverse().find((item) => item.role === 'user');
                    const workedFor = msg.role === 'assistant'
                      ? formatRoleChatWorkedFor(previousUser?.ts, msg.ts)
                      : '';
                    return (
                      <div
                        key={`${msg.ts}-${i}`}
                        className={cn('flex flex-col gap-1', msg.role === 'user' ? 'items-end' : 'items-start')}
                      >
                        {msg.role === 'assistant' && (messageProviderLabel(msg.provider) || workedFor) ? (
                          <div className="flex items-center gap-2">
                            {messageProviderLabel(msg.provider) ? (
                              <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                {messageProviderLabel(msg.provider)}
                              </span>
                            ) : null}
                            {workedFor ? (
                              <span className="text-[10px] text-gray-400 dark:text-gray-500">{workedFor}</span>
                            ) : null}
                          </div>
                        ) : null}
                        {msg.role === 'assistant' ? (
                          <div className="w-full max-w-[92%] text-sm text-gray-900 dark:text-gray-100">
                            <RoleChatTranscript
                              content={msg.content}
                              events={msg.cursorEvents}
                              roleCodes={(missionSnapshot?.nodes || []).map((node) => node.roleCode)}
                              onOpenRole={(targetRole) => {
                                const chatId = resolveRoleChatIdFromMissionNodes(
                                  missionSnapshot?.nodes || [],
                                  targetRole,
                                );
                                if (chatId) openRoleChat(targetRole, chatId);
                              }}
                            />
                          </div>
                        ) : (
                          <div className="max-w-[85%] rounded-2xl bg-gray-200 dark:bg-gray-800 px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100">
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                          </div>
                        )}
                        {msg.attachments?.length ? (
                          <div className={cn(
                            'max-w-[85%] flex flex-wrap gap-1.5',
                            msg.role === 'user' ? 'justify-end' : 'justify-start',
                          )}
                          >
                            {msg.attachments.map((attachment) => (
                              <span
                                key={attachment.id}
                                className="inline-flex items-center gap-1 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-[11px] text-gray-500 dark:text-gray-400"
                                title={attachmentLabel(attachment)}
                              >
                                <Paperclip className="h-3 w-3" />
                                {attachment.name}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        {showRunButton ? (
                          <div className="flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                            {runState?.status === 'done' ? (
                              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 text-emerald-700 dark:text-emerald-300">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Workflow run started:
                                <span className="font-mono">{runState.runId}</span>
                              </span>
                            ) : (
                              <>
                                <select
                                  value={workflowStepExecutionByMessage[i] || 'independent_agents'}
                                  onChange={(event) => setWorkflowStepExecutionByMessage((prev) => ({
                                    ...prev,
                                    [i]: event.target.value as 'independent_agents' | 'same_chat_followups',
                                  }))}
                                  className="h-7 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 text-[11px] text-gray-700 dark:text-gray-200"
                                  title="Workflow step execution strategy"
                                >
                                  <option value="independent_agents">independent_agents</option>
                                  <option value="same_chat_followups">same_chat_followups</option>
                                </select>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-7 gap-1.5 px-2 text-[11px]"
                                  disabled={runState?.status === 'running'}
                                  onClick={() => void handleRunWorkflow(i)}
                                  title="Submit this assistant turn as an ai-pipeline workflow run"
                                >
                                  {runState?.status === 'running' ? (
                                    <>
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      Submitting…
                                    </>
                                  ) : (
                                    <>
                                      <Play className="w-3.5 h-3.5" />
                                      Run this as a workflow
                                    </>
                                  )}
                                </Button>
                              </>
                            )}
                            {runState?.status === 'error' ? (
                              <span className="text-red-600 dark:text-red-400">{runState.error}</span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                  {shouldShowRoleChatStreamProgress({ sending, liveStreaming }) && !messages.some((msg) => msg.role === 'assistant' && (msg.cursorEvents?.length || msg.content)) ? (
                    <div className="flex justify-start">
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Working
                      </div>
                    </div>
                  ) : activeChat?.cursorStatus === 'running' && !liveStreaming ? (
                    <div className="flex justify-start">
                      <div className="max-w-[85%] rounded-lg bg-amber-50 dark:bg-amber-950/40 px-4 py-2 text-xs text-amber-800 dark:text-amber-200">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Agent is working. New output appears here as it lands.
                        </div>
                      </div>
                    </div>
                  ) : null}
                  </>
                )}
              </div>
              {selectedChatId && !detailLoading && !isMessagePanePinnedToBottom ? (
                <div className="absolute bottom-3 right-3">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => scrollToBottom('smooth')}
                    className="shadow-lg"
                  >
                    Jump to bottom
                  </Button>
                </div>
              ) : null}
            </div>

            {sendError ? (
              <div className="shrink-0 border-t border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-4 py-2 text-xs text-red-700 dark:text-red-300">
                {sendError}
              </div>
            ) : null}

            <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 p-3 space-y-2">
              {selectedAttachments.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {selectedAttachments.map((attachment) => (
                    <span
                      key={attachment.id}
                      className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/40 px-2 py-1 text-[11px] text-violet-700 dark:text-violet-300"
                      title={attachmentLabel(attachment)}
                    >
                      <Paperclip className="h-3 w-3" />
                      {attachment.name}
                      <button
                        type="button"
                        className="rounded-full hover:bg-violet-100 dark:hover:bg-violet-900"
                        onClick={() => removeSelectedAttachment(attachment.id)}
                        aria-label={`Remove ${attachment.name}`}
                        disabled={sending}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
              <textarea
                className="w-full min-h-[72px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                placeholder={selectedChatId ? 'Add a follow up' : 'Select or create a chat first'}
                value={draft}
                disabled={!selectedChatId || sending}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void handleSend();
                  }
                }}
              />
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    accept=".txt,.md,.markdown,.json,.yaml,.yml,.xml,.csv,.log,.ini,.conf,.properties,.env,.ts,.tsx,.js,.jsx,.py,.sh,.sql,.css,.html,text/*,application/json,application/yaml,application/x-yaml,application/xml,image/png,image/jpeg,image/webp,image/gif"
                    onChange={(event) => void handleAttachFiles(event.target.files)}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1.5 px-2 text-[11px]"
                    disabled={!selectedChatId || sending}
                    onClick={() => fileInputRef.current?.click()}
                    title="Attach AI-supported images or text/code files"
                  >
                    <Paperclip className="h-3.5 w-3.5" />
                    Attach
                  </Button>
                  <>
                    <label className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                      Model
                      <select
                        className="h-7 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 text-xs"
                        value={selectedCursorModel}
                        disabled={sending || cursorModelsLoading}
                        onChange={(e) => setSelectedCursorModel(e.target.value)}
                      >
                        {cursorModelsLoading ? <option value="">Loading models...</option> : null}
                        {!cursorModelsLoading && cursorModels.length === 0 ? <option value="">No models</option> : null}
                        {!cursorModelsLoading
                          ? cursorModels.map((model) => (
                            <option key={model.id} value={model.id}>
                              {model.label || model.id}
                            </option>
                          ))
                          : null}
                      </select>
                    </label>
                    <label className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                      Effort
                      <select
                        className="h-7 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 text-xs"
                        value={selectedCursorEffort}
                        disabled={sending || cursorModelsLoading || selectedCursorEffortOptions.length === 0}
                        onChange={(e) => setSelectedCursorEffort(e.target.value)}
                      >
                        {selectedCursorEffortOptions.length === 0 ? <option value="">No levels</option> : null}
                        {selectedCursorEffortOptions.map((level) => (
                          <option key={level.id} value={level.id}>
                            {level.label || level.id}
                          </option>
                        ))}
                      </select>
                    </label>
                  </>
                </div>
                {sending ? (
                  <Button type="button" variant="outline" onClick={handleStopSending}>
                    Stop
                  </Button>
                ) : (
                  <Button
                    type="button"
                    disabled={
                      sending
                      || creatingChat
                      || (!draft.trim() && selectedAttachments.length === 0)
                      || !selectedCursorModel
                      || cursorModelsLoading
                    }
                    onClick={() => void handleSend()}
                  >
                    Send
                  </Button>
                )}
              </div>
              {cursorModelsError ? (
                <p className="text-[11px] text-red-600 dark:text-red-400">{cursorModelsError}</p>
              ) : null}
              {messages.some((m) => m.role === 'assistant' && m.provider === 'cursor-cloud-agent') ? (
                <p className="text-[11px] text-amber-600 dark:text-amber-400">
                  Model and effort are fixed for each active AI agent. Changing either
                  starts a fresh AI agent for this chat (history is preserved on the portal
                  side; first response may take ~30-60s).
                </p>
              ) : null}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent maxWidth="md">
          <DialogHeader>
            <DialogTitle>Share chat</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Enter comma-separated email addresses for users who may read and post in this chat.
            </p>
            <textarea
              className="w-full min-h-[88px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              value={shareEmails}
              onChange={(e) => setShareEmails(e.target.value)}
              placeholder="user1@example.com, user2@example.com"
            />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShareOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleSaveShare()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent maxWidth="sm">
          <DialogHeader>
            <DialogTitle>Rename chat</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Input value={renameTitle} onChange={(e) => setRenameTitle(e.target.value)} placeholder="Title" />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRenameOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleSaveRename()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
